import React from 'react';
import { useHistory } from 'react-router-dom';
import { TextField as ReactTextField } from '@a-ui/react';
import { Field } from 'formik';
import Box from 'components/Box';
import ConfirmationModal from 'components/ConfirmationModal';
import { DetailLayout } from 'components/DetailLayout';
import { FormErrors } from 'components/Form';
import { FormHelpers } from 'components/Form/Form';
import MultiStepForm, { FormActions, FormStep } from 'components/MultiStepForm';
import PasswordField from 'components/PasswordField/PasswordField';
import Typography from 'components/Typography';
import { api } from 'hooks/api.hooks';
import { translate } from 'i18n';
import { useAuthentication } from 'state/Authentication';
import { useMessage } from 'state/Message';
import { setMessage } from 'state/Message/actions';
import { initialValues, validationSchema } from './config';
import EmailFormActions from './EmailFormActions';

const EmailForm: React.FC = () => {
  const [isModalVisible, setModalVisible] = React.useState(false);
  const { dispatch: dispatchMessage } = useMessage();
  const history = useHistory();

  const {
    state: { user },
    refreshUser,
  } = useAuthentication();

  React.useEffect(() => {
    if (user.email) {
      history.push('/gegevens/aanmeldsleutels/e-mail');
    }
  }, [user]);

  const handleConfirm = async () => {
    history.push('/gegevens');
  };

  const handleSubmit = async (
    { passwordConfirm, ...body }: typeof initialValues,
    helpers: FormHelpers,
  ) => {
    try {
      await api.post('/access-keys/email', {
        ...body,
        callbackUrl: `${window.location.origin}/gegevens/aanmeldsleutels/e-mail/callback`,
      });

      await refreshUser();

      dispatchMessage(
        setMessage('accessKeys.email.pending.message', 'accessKeysEmail', 'success', {
          email: body?.email!,
        }),
      );
      history.push(`/gegevens/aanmeldsleutels/e-mail?candidate=${body?.email!}`);
    } catch (e) {
      helpers.setFieldError('password', 'error.server.unknown');
    }
  };

  const toggleModal = () => {
    setModalVisible(!isModalVisible);
  };

  return (
    <DetailLayout
      title="accessKeys.email.add.title"
      description="accessKeys.email.add.intro"
      backAction={toggleModal}
      backLabel="accessKeys.overview.title"
    >
      <Box>
        <MultiStepForm onSubmit={handleSubmit} initialValues={initialValues}>
          <FormStep validationSchema={validationSchema[0]}>
            <Typography variant="h2" as="h6" classNames="u-margin-bottom">
              {translate('accessKeys.email.add.step1.title')}
            </Typography>

            <FormErrors />
            <Field name="email" label={translate('input.label.email')} as={ReactTextField} />
          </FormStep>

          <FormStep validationSchema={validationSchema[1]}>
            <Typography variant="h2" as="h6" classNames="u-margin-bottom-xs">
              {translate('accessKeys.email.add.step2.title')}
            </Typography>

            <Typography classNames="u-margin-bottom">
              {translate('accessKeys.email.add.step2.description')}
            </Typography>

            <FormErrors />

            <PasswordField
              label={translate('input.label.password')}
              name="password"
              classNames="u-margin-bottom"
              showRequirements
            />

            <PasswordField
              label={translate('input.label.password_confirm')}
              name="passwordConfirm"
            />
          </FormStep>

          <FormActions>
            <EmailFormActions onCancel={toggleModal} />
          </FormActions>
        </MultiStepForm>
      </Box>

      <ConfirmationModal
        title="accessKeys.email.add.modal.title"
        body="accessKeys.email.add.modal.description"
        confirmLabel="common.modal.confirm"
        cancelLabel="common.modal.cancel"
        onRequestClose={toggleModal}
        onConfirm={handleConfirm}
        isOpen={isModalVisible}
      />
    </DetailLayout>
  );
};

export default EmailForm;
